import React, { useState } from 'react';
import './App.css';
import BenchmarkDashboard from './components/BenchmarkDashboard';

function App() {
  const [domain, setDomain] = useState('');
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [linkedinLoading, setLinkedinLoading] = useState<{ [key: string]: boolean }>({});

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setData(null);

    try {
      // Use query parameter to handle URLs with protocols properly
      const response = await fetch(`/api/domain?domain=${encodeURIComponent(domain)}`);
      if (!response.ok) {
        const errorData = await response.json();
        
        // Handle structured error response
        if (typeof errorData.detail === 'object' && errorData.detail.error) {
          let errorMessage = errorData.detail.error;
          
          // Add suggestion if available
          if (errorData.detail.suggestion) {
            errorMessage += `\n\n${errorData.detail.suggestion}`;
          }
          
          // Add alternatives if available
          if (errorData.detail.alternatives && errorData.detail.alternatives.length > 0) {
            errorMessage += `\n\nAlternatives: ${errorData.detail.alternatives.join(', ')}`;
          }
          
          throw new Error(errorMessage);
        } else if (typeof errorData.detail === 'string') {
          throw new Error(errorData.detail);
        } else {
          throw new Error('Something went wrong');
        }
      }
      const result = await response.json();
      setData(result);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleLinkedInSearch = async (companyDomain: string) => {
    setLinkedinLoading(prev => ({ ...prev, [companyDomain]: true }));
    
    try {
      const response = await fetch(`/api/linkedin/company/${companyDomain}`);
      if (!response.ok) {
        throw new Error('Failed to fetch LinkedIn data');
      }
      const result = await response.json();
      
      // Update the company data with LinkedIn info
      setData((prevData: any) => ({
        ...prevData,
        companies: prevData.companies.map((company: any) => 
          company.domain === companyDomain 
            ? { ...company, linkedin_company_data: result.company_result }
            : company
        )
      }));
    } catch (err: any) {
      console.error('LinkedIn lookup failed:', err.message);
    } finally {
      setLinkedinLoading(prev => ({ ...prev, [companyDomain]: false }));
    }
  };

  return (
    <div className="App">
      <header className="App-header">
        <h1>PubSearch - Sales Research Tool</h1>
        <p>Discover advertising partners and find key contacts for sales outreach</p>
      </header>
      <main>
        <form onSubmit={handleSubmit}>
          <input
            type="text"
            value={domain}
            onChange={(e) => setDomain(e.target.value)}
            placeholder="Enter a domain or URL (e.g., nytimes.com or https://nytimes.com)"
          />
          <button type="submit" disabled={loading}>
            {loading ? 'Loading...' : 'Analyze'}
          </button>
        </form>

        {error && <div className="error">{error}</div>}

        {data && (
          <div className="results">
            <h2>Sales Research Results for {data.domain}</h2>
            
            {data.summary && (
              <div className="summary-stats">
                <h3>Summary</h3>
                <div className="stats-grid">
                  <div className="stat">
                    <span className="stat-value">{data.summary.unique_companies}</span>
                    <span className="stat-label">Companies Found</span>
                  </div>
                  <div className="stat">
                    <span className="stat-value">{data.summary.direct_relationships}</span>
                    <span className="stat-label">Direct Partners</span>
                  </div>
                  <div className="stat">
                    <span className="stat-value">{data.summary.reseller_relationships}</span>
                    <span className="stat-label">Reseller Partners</span>
                  </div>
                </div>
              </div>
            )}

            {data.sincera_data && !data.sincera_data.error && (
              <div className="sincera-metrics-detailed">
                <h3>📊 Sincera Publisher Analytics</h3>
                <div className="sincera-header">
                  <h4>{data.sincera_data.name}</h4>
                  <p className="sincera-description">{data.sincera_data.pub_description}</p>
                </div>
                
                <div className="metrics-grid">
                  <div className="metric-card highlight">
                    <div className="metric-value">{(data.sincera_data.avg_ads_to_content_ratio * 100).toFixed(2)}%</div>
                    <div className="metric-label">Ads-to-Content Ratio</div>
                    <div className="metric-sublabel">Industry standard: 2-5%</div>
                  </div>
                  
                  <div className="metric-card">
                    <div className="metric-value">{data.sincera_data.total_supply_paths ? data.sincera_data.total_supply_paths.toLocaleString() : 'N/A'}</div>
                    <div className="metric-label">Supply Paths</div>
                    <div className="metric-sublabel">Monetization routes</div>
                  </div>
                  
                  <div className="metric-card">
                    <div className="metric-value">{data.sincera_data.reseller_count ? data.sincera_data.reseller_count.toLocaleString() : 'N/A'}</div>
                    <div className="metric-label">Resellers</div>
                    <div className="metric-sublabel">Distribution partners</div>
                  </div>
                  
                  <div className="metric-card">
                    <div className="metric-value">{data.sincera_data.avg_ads_in_view ? data.sincera_data.avg_ads_in_view.toFixed(2) : 'N/A'}</div>
                    <div className="metric-label">Avg Ads in View</div>
                    <div className="metric-sublabel">Viewability metric</div>
                  </div>
                  
                  <div className="metric-card">
                    <div className="metric-value">{data.sincera_data.avg_ad_refresh ? data.sincera_data.avg_ad_refresh.toFixed(1) + 's' : 'N/A'}</div>
                    <div className="metric-label">Ad Refresh Rate</div>
                    <div className="metric-sublabel">Revenue optimization</div>
                  </div>
                  
                  <div className="metric-card">
                    <div className="metric-value">{data.sincera_data.total_unique_gpids ? data.sincera_data.total_unique_gpids.toLocaleString() : 'N/A'}</div>
                    <div className="metric-label">Unique GPIDs</div>
                    <div className="metric-sublabel">Ad inventory diversity</div>
                  </div>
                  
                  <div className="metric-card">
                    <div className="metric-value">{data.sincera_data.id_absorption_rate ? (data.sincera_data.id_absorption_rate * 100).toFixed(1) + '%' : 'N/A'}</div>
                    <div className="metric-label">ID Absorption Rate</div>
                    <div className="metric-sublabel">Identity resolution</div>
                  </div>
                  
                  <div className="metric-card">
                    <div className="metric-value">{data.sincera_data.avg_page_weight ? data.sincera_data.avg_page_weight.toFixed(1) + 'MB' : 'N/A'}</div>
                    <div className="metric-label">Avg Page Weight</div>
                    <div className="metric-sublabel">Performance impact</div>
                  </div>
                  
                  <div className="metric-card">
                    <div className="metric-value">{data.sincera_data.avg_cpu ? data.sincera_data.avg_cpu.toFixed(1) + '%' : 'N/A'}</div>
                    <div className="metric-label">Avg CPU Usage</div>
                    <div className="metric-sublabel">Resource consumption</div>
                  </div>
                </div>
                
                <div className="sincera-insights">
                  <h4>🎯 Key Insights</h4>
                  <div className="insights-grid">
                    <div className="insight-item">
                      <strong>Revenue Optimization:</strong> 
                      {data.sincera_data.avg_ads_to_content_ratio > 0.05 ? 
                        " High ad density suggests strong monetization focus" : 
                        " Conservative ad approach prioritizing user experience"
                      }
                    </div>
                    <div className="insight-item">
                      <strong>Market Reach:</strong> 
                      {data.sincera_data.total_supply_paths && data.sincera_data.total_supply_paths > 200 ? 
                        " Extensive supply path network for maximum reach" : 
                        " Focused supply strategy with selected partners"
                      }
                    </div>
                    <div className="insight-item">
                      <strong>Performance:</strong> 
                      {data.sincera_data.avg_page_weight && data.sincera_data.avg_page_weight < 10 ? 
                        " Lightweight pages for fast loading" : 
                        " Rich content experience with higher resource usage"
                      }
                    </div>
                  </div>
                </div>
              </div>
            )}

            {data.sincera_data && !data.sincera_data.error && (
              <BenchmarkDashboard sinceraData={data.sincera_data} />
            )}

            {data.companies && (
              <div className="companies-data">
                <h3>Companies for LinkedIn Research</h3>
                <div className="companies-grid">
                  {data.companies.map((company: any, index: number) => (
                    <div key={index} className="company-card">
                      <h4>{company.company_name}</h4>
                      <p><strong>Domain:</strong> {company.domain}</p>
                      <p><strong>Type:</strong> {company.company_type}</p>
                      <p><strong>Relationship:</strong> {company.relationship_types.join(", ")}</p>
                      <p><strong>Records:</strong> {company.record_count}</p>
                      
                      <button 
                        className="linkedin-btn" 
                        onClick={() => handleLinkedInSearch(company.domain)}
                        disabled={linkedinLoading[company.domain]}
                      >
                        {linkedinLoading[company.domain] ? 'Loading LinkedIn...' : 'Find LinkedIn Company'}
                      </button>

                      {company.linkedin_search_urls && (
                        <div className="linkedin-search-urls">
                          <h5>LinkedIn Search URLs:</h5>
                          {company.linkedin_search_urls.map((searchUrl: any, idx: number) => (
                            <div key={idx} className="search-url">
                              <a href={searchUrl.url} target="_blank" rel="noopener noreferrer">
                                {searchUrl.title}
                              </a>
                              <span className="search-type">({searchUrl.search_type})</span>
                            </div>
                          ))}
                        </div>
                      )}

                      {company.linkedin_company_data && (
                        <div className="linkedin-company-data">
                          <h5>Company Data {company.linkedin_company_data.source && `(${company.linkedin_company_data.source})`}:</h5>
                          {company.linkedin_company_data.success ? (
                            <div className="company-details">
                              <p><strong>Company:</strong> {company.linkedin_company_data.company_data?.company_name || 'N/A'}</p>
                              <p><strong>Industry:</strong> {
                                company.linkedin_company_data.company_data?.industries?.join(', ') || 
                                company.linkedin_company_data.company_data?.industry || 'N/A'
                              }</p>
                              <p><strong>Size:</strong> {
                                company.linkedin_company_data.company_data?.employee_range || 
                                company.linkedin_company_data.company_data?.employee_count?.toLocaleString() || 'N/A'
                              }</p>
                              <p><strong>Founded:</strong> {company.linkedin_company_data.company_data?.year_founded || 'N/A'}</p>
                              <p><strong>Website:</strong> {company.linkedin_company_data.company_data?.website || 'N/A'}</p>
                              {company.linkedin_company_data.company_data?.tagline && (
                                <p><strong>Tagline:</strong> {company.linkedin_company_data.company_data.tagline}</p>
                              )}
                              {company.linkedin_company_data.company_data?.description && (
                                <div>
                                  <p><strong>About:</strong></p>
                                  <p className="company-description">{company.linkedin_company_data.company_data.description}</p>
                                </div>
                              )}
                              {company.linkedin_company_data.company_data?.employee_count && (
                                <p><strong>Employees:</strong> {company.linkedin_company_data.company_data.employee_count.toLocaleString()}</p>
                              )}
                              {company.linkedin_company_data.company_data?.follower_count && (
                                <p><strong>LinkedIn Followers:</strong> {company.linkedin_company_data.company_data.follower_count.toLocaleString()}</p>
                              )}
                              {company.linkedin_company_data.company_data?.followers && (
                                <p><strong>Followers:</strong> {company.linkedin_company_data.company_data.followers.toLocaleString()}</p>
                              )}
                              {(company.linkedin_company_data.company_data?.hq_full_address || company.linkedin_company_data.company_data?.headquarters) && (
                                <p><strong>Headquarters:</strong> {
                                  company.linkedin_company_data.company_data.hq_full_address || 
                                  company.linkedin_company_data.company_data.headquarters
                                }</p>
                              )}
                              {company.linkedin_company_data.company_data?.specialties && (
                                <p><strong>Specialties:</strong> {company.linkedin_company_data.company_data.specialties}</p>
                              )}
                            </div>
                          ) : (
                            <p className="error">Company lookup failed: {company.linkedin_company_data.error}</p>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            <details className="raw-data">
              <summary>Raw Data (Click to Expand)</summary>
              {data.ads_txt && (
                <div className="ads-txt-data">
                  <h4>Ads.txt Records</h4>
                  <pre>{JSON.stringify(data.ads_txt, null, 2)}</pre>
                </div>
              )}
              {data.sincera_data && !data.sincera_data.error && (
                <div className="sincera-raw">
                  <h4>OpenSincera Raw Data</h4>
                  <pre>{JSON.stringify(data.sincera_data, null, 2)}</pre>
                </div>
              )}
            </details>
          </div>
        )}
      </main>
    </div>
  );
}

export default App;